import { useState } from "react";
import { useUI } from "../../lib/store";
import { ProjectList } from "../projects/ProjectList";
import { ProjectForm } from "../projects/ProjectForm";
import { TaskListPanel } from "./TaskListPanel";
import { UsageMeter } from "../shared/UsageMeter";
import { IconButton } from "../shared/IconButton";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from "../shared/Icon";
import { cn } from "../../lib/cn";
import type { Project } from "../../lib/types";

interface Props {
  collapsed: boolean;
}

export function Sidebar({ collapsed }: Props) {
  const toggle = useUI((s) => s.toggleSidebar);
  const showArchived = useUI((s) => s.showArchivedProjects);
  const setShowArchived = useUI((s) => s.setShowArchivedProjects);
  const leftPanelView = useUI((s) => s.leftPanelView);
  const setLeftPanelView = useUI((s) => s.setLeftPanelView);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  // In the rail (collapsed) state there's no room for the Tasks list — the
  // project monograms are the only thing that fits — so the panel is always
  // Projects when collapsed.
  const showTasks = !collapsed && leftPanelView === "tasks";

  return (
    <>
      <div className="h-11 flex items-center justify-between px-3 border-b border-border-subtle gap-2">
        {!collapsed ? (
          <PanelSwitch active={leftPanelView} onChange={setLeftPanelView} />
        ) : (
          <span className="sr-only">Projects</span>
        )}
        <div className={cn("flex items-center gap-1", collapsed && "mx-auto")}>
          {!collapsed && leftPanelView === "projects" && (
            <IconButton
              icon={showArchived ? <EyeOff /> : <Eye />}
              label={
                showArchived ? "Hide archived projects" : "Show archived projects"
              }
              onClick={() => setShowArchived(!showArchived)}
              size="sm"
              className={cn(
                showArchived && "text-accent hover:text-accent-hover",
              )}
            />
          )}
          <IconButton
            icon={collapsed ? <ChevronRight /> : <ChevronLeft />}
            label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={toggle}
            size="sm"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto pt-1.5">
        {showTasks ? (
          <TaskListPanel />
        ) : (
          <ProjectList
            collapsed={collapsed}
            showArchived={showArchived}
            onNew={() => setCreating(true)}
            onEdit={setEditing}
          />
        )}
      </div>
      {!collapsed && (
        <div className="border-t border-border-subtle">
          <UsageMeter />
        </div>
      )}
      {creating && <ProjectForm onClose={() => setCreating(false)} />}
      {editing && (
        <ProjectForm project={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function PanelSwitch({
  active,
  onChange,
}: {
  active: "projects" | "tasks";
  onChange: (v: "projects" | "tasks") => void;
}) {
  const opts: { id: "projects" | "tasks"; label: string }[] = [
    { id: "projects", label: "Projects" },
    { id: "tasks", label: "Tasks" },
  ];
  return (
    <div
      className="flex items-center gap-3"
      role="tablist"
      aria-label="Sidebar panel"
    >
      {opts.map((o) => {
        const on = o.id === active;
        return (
          <button
            key={o.id}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.id)}
            className={cn(
              "relative h-7 px-0.5 text-caption bg-transparent transition-colors duration-fast ease-out",
              on
                ? "text-text-primary"
                : "text-text-tertiary hover:text-text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-panel",
            )}
          >
            {o.label}
            {on && (
              <span
                aria-hidden="true"
                className="absolute -bottom-2 left-0 right-0 h-[2px] bg-accent rounded-full"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
